import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { AlertTriangle, CalendarClock, CalendarPlus, CheckCircle2, Clock3, MessageSquareText, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { fmtDate, initials } from '../lib/dates';
import { StudentPhoto } from '../components/Photos';
import type { ExamForecast, ExamForecastsResponse } from '../types';
import { useUiStore } from '../store/ui';
import { Button, Card, SelectField, TextAreaField, cx } from '../components/charte';
import { Sheet } from '../components/shell/Sheet';

// ─── Prévisions d'examen ─────────────────────────────────────────────────────
// Le moniteur indique la dizaine où son élève sera prêt pour l'examen pratique ; Permio en fait
// la demande de réservation automatique (centres favoris de l'école), ou dit ce qui manque.
// Refonte du 14/10/2026 (tablette d'abord) : la liste d'abord, « Nouvelle prévision » dans une fenêtre.
// Note pour le secrétariat (16/10/2026) : jointe à la prévision (« il faudrait qu'il effectue 8 h de plus
// d'ici là »), lue au bureau ; changée seule, elle ne relance pas la demande de réservation.

/** Longueur maximale de la note (serveur : FORECAST_NOTE_MAX_LENGTH). */
const NOTE_MAX_LENGTH = 500;

const MONTH_FORMAT = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });
const monthLabel = (month: string) => {
  const label = MONTH_FORMAT.format(new Date(`${month}-15T12:00:00Z`));
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const STATUS: Record<ExamForecast['status'], { label: string; className: string; Icon: typeof Clock3 }> = {
  pending: { label: 'Transmise à Permio', className: 'bg-primary-50 text-primary-700', Icon: Sparkles },
  requested: { label: 'Demande de réservation créée', className: 'bg-success-light text-success-dark', Icon: CheckCircle2 },
  blocked: { label: 'À compléter par le secrétariat', className: 'bg-amber-50 text-amber-800', Icon: AlertTriangle },
  expired: { label: 'Dizaine passée', className: 'bg-surface-2 text-secondary', Icon: Clock3 },
};

/**
 * Une demande ou un examen déjà prévu (`already_engaged`) n'a rien à compléter : badge neutre, comme
 * la ligne « Rien à compléter » que le secrétariat lit sur son accueil (« À traiter »).
 */
const ALREADY_ENGAGED = { label: 'Demande ou examen déjà prévu', className: 'bg-surface-2 text-body', Icon: CalendarClock };
const badgeOf = (forecast: ExamForecast) =>
  forecast.status === 'blocked' && forecast.reason === 'already_engaged' ? ALREADY_ENGAGED : STATUS[forecast.status];

const errorMessage = (error: unknown, fallback: string) =>
  (isAxiosError(error) && (error.response?.data as { message?: string | string[] } | undefined)?.message?.toString()) || fallback;

function ForecastCard({
  forecast,
  onEdit,
  onRemove,
  removing,
}: {
  forecast: ExamForecast;
  onEdit: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  const status = badgeOf(forecast);
  const [first = '', ...rest] = forecast.candidateName.split(' ');
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="flex flex-col rounded-card bg-white p-5 shadow-card" aria-label={`Prévision de ${forecast.candidateName}`}>
      <div className="flex items-start gap-3">
        <span className="relative grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full bg-avatar text-[13px] font-semibold text-primary-700">
          {initials(first, rest.join(' ') || first)}
          <StudentPhoto candidateId={forecast.candidateId} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-heading">{forecast.candidateName}</p>
          <p className="mt-0.5 text-sm text-body">
            Prêt·e pour l'examen : <span className="font-semibold">{forecast.label}</span>
          </p>
          <p className="text-xs text-secondary">{forecast.range}</p>
        </div>
      </div>
      {forecast.note && (
        <p className="mt-3 flex gap-2 whitespace-pre-line rounded-row bg-surface-light px-3 py-2 text-[13px] leading-relaxed text-body">
          <MessageSquareText size={15} className="mt-0.5 shrink-0 text-secondary" aria-hidden />
          <span>
            <span className="font-semibold">Note pour le secrétariat :</span> {forecast.note}
          </span>
        </p>
      )}
      <span className={cx('mt-3 inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-semibold', status.className)}>
        <status.Icon size={13} aria-hidden />
        {status.label}
        {forecast.status === 'requested' && forecast.handledAt ? ` le ${fmtDate(forecast.handledAt)}` : ''}
      </span>
      {forecast.status === 'pending' && <p className="mt-2 text-xs text-secondary">Permio prépare la demande de réservation sur cette dizaine…</p>}
      {forecast.status === 'blocked' && (
        <p className={cx('mt-2 text-xs leading-relaxed', status === ALREADY_ENGAGED ? 'text-secondary' : 'text-amber-800')}>
          {forecast.reasonLabel ?? 'La demande n’a pas pu être créée.'} Le secrétariat le voit sur son accueil, dans « À traiter ». Permio revoit
          cette prévision chaque heure.
        </p>
      )}
      {forecast.status === 'requested' && (
        <p className="mt-2 text-xs text-secondary">Le secrétariat la suit dans « Réservation d’examen » et peut l’ajuster.</p>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
        <Button size="sm" icon={Pencil} onClick={onEdit}>
          Modifier
        </Button>
        {confirming ? (
          <>
            <span className="text-xs text-secondary">
              {forecast.requestCreated ? 'La demande déjà créée restera au secrétariat.' : 'Retirer cette prévision ?'}
            </span>
            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              className="inline-flex h-8 items-center rounded-pill bg-red-500 px-3.5 text-[13px] font-semibold text-white hover:bg-red-600 disabled:opacity-60"
            >
              {removing ? 'Retrait…' : 'Retirer'}
            </button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              Annuler
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" icon={Trash2} onClick={() => setConfirming(true)} className="enabled:hover:!bg-danger-light enabled:hover:!text-danger-dark">
            Retirer la prévision
          </Button>
        )}
      </div>
    </li>
  );
}

export default function ForecastsView() {
  const queryClient = useQueryClient();
  const students = useQuery({ queryKey: ['students'], queryFn: instructorApi.getStudents });
  const forecasts = useQuery({
    queryKey: ['exam-forecasts'],
    queryFn: instructorApi.getExamForecasts,
    // Tant que Permio prépare une demande, l'écran suit son avancement.
    refetchInterval: (query) => (query.state.data?.items.some((f) => f.status === 'pending') ? 4_000 : 60_000),
  });
  const periods = forecasts.data?.periods ?? [];
  const months = useMemo(() => [...new Set(periods.map((p) => p.month))], [periods]);

  const [formOpen, setFormOpen] = useState(false);
  const [candidateId, setCandidateId] = useState('');
  const [month, setMonth] = useState('');
  const [third, setThird] = useState<1 | 2 | 3 | null>(null);
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  // Ouvert depuis le panneau d'un élève : fenêtre ouverte, élève présélectionné.
  const preset = useUiStore((s) => s.forecastCandidateId);
  const clearPreset = useUiStore((s) => s.clearForecast);
  useEffect(() => {
    if (!preset) return;
    setCandidateId(preset);
    setNote(forecasts.data?.items.find((f) => f.candidateId === preset)?.note ?? '');
    setThird(null);
    setError('');
    setFormOpen(true);
    clearPreset();
  }, [preset, clearPreset]);

  useEffect(() => {
    if (!month && months.length) setMonth(months[0]);
  }, [month, months]);
  const available = (t: 1 | 2 | 3) => periods.find((p) => p.month === month && p.third === t);
  useEffect(() => {
    if (third && !available(third)) setThird(null);
  }, [month, periods]);

  const current = forecasts.data?.items.find((f) => f.candidateId === candidateId);
  const updated = (data: ExamForecastsResponse) => queryClient.setQueryData(['exam-forecasts'], data);
  // Un autre élève choisi : sa note actuelle, pour ne pas l'effacer sans le voir.
  const pickStudent = (id: string) => {
    setCandidateId(id);
    setNote(forecasts.data?.items.find((f) => f.candidateId === id)?.note ?? '');
  };

  const save = useMutation({
    // Même dizaine : seule la note change, Permio n'est pas relancé.
    mutationFn: (v: { noteOnly: boolean; note: string | null }) => instructorApi.setExamForecast(candidateId, month, third!, v.note),
    onMutate: () => {
      setError('');
      setNotice('');
    },
    onSuccess: (data, v) => {
      updated(data);
      const name = data.items.find((f) => f.candidateId === candidateId)?.candidateName ?? 'l’élève';
      setNotice(
        !v.noteOnly
          ? `Prévision enregistrée pour ${name} : Permio prépare la demande de réservation automatique.`
          : v.note
            ? `Note enregistrée pour ${name} : le secrétariat la lit avec la prévision.`
            : `Prévision de ${name} enregistrée, sans note.`,
      );
      setCandidateId('');
      setThird(null);
      setNote('');
      setFormOpen(false);
    },
    onError: (e) => setError(errorMessage(e, 'La prévision n’a pas pu être enregistrée.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => instructorApi.clearExamForecast(id),
    onSuccess: (data) => {
      updated(data);
      setNotice('Prévision retirée.');
    },
    onError: (e) => setError(errorMessage(e, 'La prévision n’a pas pu être retirée.')),
  });

  const openNew = () => {
    setCandidateId('');
    setThird(null);
    setNote('');
    setError('');
    setNotice('');
    setFormOpen(true);
  };
  const edit = (f: ExamForecast) => {
    setCandidateId(f.candidateId);
    setNote(f.note ?? '');
    if (periods.some((p) => p.month === f.month)) {
      setMonth(f.month);
      setThird(f.third);
    }
    setNotice('');
    setError('');
    setFormOpen(true);
  };

  const items = forecasts.data?.items ?? [];
  const canSave = !!candidateId && !!month && !!third && !save.isPending;

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h1 className="text-title-md text-heading sm:text-title-lg">Prévisions d'examen</h1>
          <p className="mt-1 text-sm leading-relaxed text-secondary">
            Indiquez la dizaine où chaque élève sera prêt pour l'examen pratique. Permio crée alors la demande de réservation
            automatique sur cette période, avec les centres d'examen favoris de l'école.
          </p>
        </div>
        <Button variant="primary" icon={CalendarPlus} onClick={openNew}>
          Nouvelle prévision
        </Button>
      </header>

      {notice && (
        <p role="status" className="rounded-row bg-surface-indigo px-4 py-3 text-sm text-primary-900 ring-1 ring-primary-100">
          {notice}
        </p>
      )}
      {error && !formOpen && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          {error}
        </p>
      )}

      <div>
        <h2 className="mb-3 text-title-sm text-heading">Prévisions de vos élèves</h2>
        {forecasts.isLoading ? (
          <p className="text-secondary">Chargement des prévisions…</p>
        ) : forecasts.isError ? (
          <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
            Impossible de charger les prévisions.
          </p>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center text-secondary">
            <p>Aucune prévision pour le moment.</p>
            <Button variant="link" className="mt-2" onClick={openNew}>
              Poser une première prévision
            </Button>
          </Card>
        ) : (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((f) => (
              <ForecastCard
                key={f.candidateId}
                forecast={f}
                onEdit={() => edit(f)}
                onRemove={() => remove.mutate(f.candidateId)}
                removing={remove.isPending && remove.variables === f.candidateId}
              />
            ))}
          </ul>
        )}
      </div>

      {formOpen && (
        <Sheet title="Nouvelle prévision" description="Élève, mois, puis dizaine." onClose={() => setFormOpen(false)} className="sm:max-w-lg">
          <form
            className="space-y-5"
            aria-label="Nouvelle prévision d'examen"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSave)
                save.mutate({
                  noteOnly: !!current && current.month === month && current.third === third,
                  note: note.trim() ? note : null,
                });
            }}
          >
            <SelectField label="Élève" value={candidateId} onChange={(e) => pickStudent(e.target.value)} className="[&_select]:h-11">
              <option value="">Choisir un élève</option>
              {(students.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firstName} {s.lastName}
                </option>
              ))}
            </SelectField>
            <SelectField label="Mois" value={month} onChange={(e) => setMonth(e.target.value)} className="[&_select]:h-11">
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </SelectField>
            <fieldset>
              <legend className="mb-1.5 text-[12.5px] font-medium text-secondary">Dizaine</legend>
              <div className="grid gap-2 sm:grid-cols-3">
                {([1, 2, 3] as const).map((t) => {
                  const period = available(t);
                  const on = third === t;
                  return (
                    <label
                      key={t}
                      className={cx(
                        'flex min-h-[56px] items-center gap-3 rounded-row border px-4 py-3 text-sm transition-colors',
                        !period
                          ? 'cursor-not-allowed border-slate-100 bg-surface-light text-muted'
                          : on
                            ? 'cursor-pointer border-primary-300 bg-primary-50 text-primary-900'
                            : 'cursor-pointer border-line text-body hover:bg-surface-light',
                      )}
                    >
                      <input type="radio" name="third" className="h-4 w-4 accent-primary" checked={on} disabled={!period} onChange={() => setThird(t)} />
                      <span>
                        <span className="block font-semibold">{t === 1 ? '1re dizaine' : t === 2 ? '2e dizaine' : '3e dizaine'}</span>
                        <span className="block text-xs">{period ? period.range : 'Déjà passée'}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <TextAreaField
              label="Note pour le secrétariat (facultatif)"
              placeholder="Ex. : il faudrait qu’il effectue 8 h de plus d’ici là."
              hint={`Le secrétariat la lit avec la prévision. ${note.length}/${NOTE_MAX_LENGTH} caractères.`}
              value={note}
              maxLength={NOTE_MAX_LENGTH}
              rows={3}
              onChange={(e) => setNote(e.target.value)}
            />
            {current && <p className="text-xs text-secondary">Remplace la prévision actuelle de cet élève ({current.label}).</p>}
            {error && (
              <p role="alert" className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
                {error}
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" onClick={() => setFormOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" className="ml-auto" disabled={!canSave} loading={save.isPending}>
                Enregistrer la prévision
              </Button>
            </div>
          </form>
        </Sheet>
      )}
    </section>
  );
}
